import React, { useState, useEffect } from 'react';
import { Download, BarChart3, Users, Mail, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BAND_COLORS = {
  Developing: 'bg-slate-200 text-slate-700',
  Emerging: 'bg-amber-100 text-amber-700',
  Established: 'bg-blue-100 text-blue-700',
  Advanced: 'bg-emerald-100 text-emerald-700',
};

const AdminCFQ = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cfq/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setStats(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchStats();
  }, []);

  const handleExport = (type) => {
    const url = type === 'pulse' ? `${API_URL}/api/cfq/admin/export` : `${API_URL}/api/cfq/admin/mailing-export`;
    window.open(`${url}?token=${token}`, '_blank');
  };

  if (loading) return <div className="py-16 text-center text-cream/20">Loading...</div>;
  if (!stats) return <div className="py-16 text-center text-cream/30">Failed to load CFQ analytics</div>;

  const pillars = ['self_anchoring', 'perspective_shifting', 'adaptive_communication', 'relational_integration'];
  const pillarLabels = { self_anchoring: 'Self-Anchoring', perspective_shifting: 'Perspective-Shifting', adaptive_communication: 'Adaptive Communication', relational_integration: 'Relational Integration' };

  return (
    <div data-testid="admin-cfq">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">CFQ Analytics</h1>
          <p className="text-cream/30 text-sm mt-1">Anonymous pulse data and mailing list — no individual answers</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleExport('pulse')} className="inline-flex items-center gap-1.5 px-3 py-2 border border-cream/10 text-cream/50 text-xs rounded-lg hover:border-gold/20 hover:text-gold" data-testid="export-pulse-btn">
            <Download size={14} /> Export Pulse CSV
          </button>
          <button onClick={() => handleExport('mailing')} className="inline-flex items-center gap-1.5 px-3 py-2 border border-cream/10 text-cream/50 text-xs rounded-lg hover:border-gold/20 hover:text-gold" data-testid="export-mailing-btn">
            <Mail size={14} /> Export Mailing CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { label: 'Total Completions', value: stats.total_completions, Icon: BarChart3 },
          { label: 'Unique Sessions', value: stats.by_session?.length || 0, Icon: Users },
          { label: 'Mailing List', value: stats.mailing_list_count, Icon: Mail },
          { label: 'Days Tracked', value: stats.by_day?.length || 0, Icon: Calendar },
        ].map((card) => (
          <div key={card.label} className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <card.Icon size={16} className="text-gold/40 mb-3" />
            <p className="text-2xl text-cream font-light">{card.value}</p>
            <p className="text-cream/20 text-xs mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Band Distribution */}
        <div className="bg-navy border border-gold/[0.06] rounded-xl p-6">
          <h3 className="text-cream text-sm font-semibold mb-4">Pillar Band Distribution</h3>
          <div className="space-y-5">
            {pillars.map((key) => {
              const bands = stats.band_totals?.[key] || {};
              const total = Object.values(bands).reduce((s, v) => s + v, 0) || 1;
              return (
                <div key={key}>
                  <p className="text-cream/50 text-xs font-medium mb-2">{pillarLabels[key]}</p>
                  <div className="flex gap-1 h-5 rounded overflow-hidden mb-1.5">
                    {['Developing', 'Emerging', 'Established', 'Advanced'].map((band) => {
                      const count = bands[band] || 0;
                      const pct = (count / total) * 100;
                      if (pct === 0) return null;
                      return (
                        <div key={band} className={`${BAND_COLORS[band]} flex items-center justify-center text-[9px] font-medium rounded-sm`} style={{ width: `${pct}%` }} title={`${band}: ${count}`}>
                          {pct > 15 ? `${Math.round(pct)}%` : ''}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-3 text-[10px] text-cream/20">
                    {['Developing', 'Emerging', 'Established', 'Advanced'].map((band) => (
                      <span key={band}>{band}: {bands[band] || 0}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Completions by Session */}
        <div className="bg-navy border border-gold/[0.06] rounded-xl p-6">
          <h3 className="text-cream text-sm font-semibold mb-4">Completions by Session</h3>
          {stats.by_session?.length === 0 ? (
            <p className="text-cream/20 text-sm py-8 text-center">No pulse data yet</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {stats.by_session?.map((s) => (
                <div key={s.session_code} className="flex items-center justify-between py-2 px-3 bg-navy-deep rounded">
                  <span className="text-cream/50 text-sm font-mono">{s.session_code}</span>
                  <span className="text-gold text-sm font-medium">{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Completions by Day */}
        <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 lg:col-span-2">
          <h3 className="text-cream text-sm font-semibold mb-4">Completions by Day (Last 30)</h3>
          {stats.by_day?.length === 0 ? (
            <p className="text-cream/20 text-sm py-8 text-center">No data yet</p>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {stats.by_day?.slice().reverse().map((d) => {
                const maxCount = Math.max(...stats.by_day.map(x => x.count));
                const height = maxCount > 0 ? (d.count / maxCount) * 100 : 0;
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                    <span className="text-cream/30 text-[8px]">{d.count}</span>
                    <div className="w-full bg-gold/30 rounded-t" style={{ height: `${Math.max(height, 4)}%` }} />
                    <span className="text-cream/15 text-[7px] -rotate-45 origin-left whitespace-nowrap">{d.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCFQ;
